import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
  OwlDateTimeComponent,
  OwlDateTimeFormats,
} from '@danielmoncada/angular-datetime-picker';
import { PickerType } from '@danielmoncada/angular-datetime-picker';
import moment, { Moment } from 'moment';
import { Subscription, filter, take } from 'rxjs';
import { Language } from '../../common/constants/language';
import { IconNames } from '../../common/constants/icon-names';
import {
  DATE_TIME_PICKER_DISPLAY_DATE_12H_FORMAT,
  DATE_TIME_PICKER_DISPLAY_DATE_FORMAT,
  DATE_TIMEPICKER_CONFIG,
  DATEPICKER_DISPLAY_DATE_FORMAT,
  DATEPICKER_DISPLAY_HOUR_ONLY_FORMAT,
  LOCALE_CODE_LIST,
} from '../../common/constants/date-config';
import {
  getCurrentTimeWithTimezone,
  markRequiredControlWithAsteriskSymbol,
  markAsTouchedAndUpdate,
} from '../../common';
import { AutoUnsubscribe } from '../../common/decorators/auto-unsubscribe.decorator';
import { AppFacade, AuthFacade } from '../../store';
import { DateModule } from '../../date.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-datepicker',
  imports: [TranslateModule, DateModule, MatTooltipModule, SvgIconComponent],
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: (): OwlDateTimeFormats => ({ ...DATE_TIMEPICKER_CONFIG }),
    },
  ],
})
@AutoUnsubscribe
export class DatepickerComponent implements OnInit, ControlValueAccessor {
  @ViewChild('datetimePicker') picker!: OwlDateTimeComponent<Moment>;
  @Input() label: string;
  @Input() min?: string | Date;
  @Input() max?: string | Date;
  @Input() disabled?: boolean;
  @Input() errorMessage?: string;
  @Input() fontIcon = IconNames.CalendarClock;
  @Input() testId?: string;
  @Input() pickerType: PickerType = 'both';
  @Input() showWarningIcon: boolean;
  @Input() warningTooltip: string;
  @Input() timezoneText?: string;
  @Input() changeHourOnly = false;

  @Input() set timezone(value: string) {
    if (value) {
      this.startTime = new Date(getCurrentTimeWithTimezone(value));
    }
  }

  @Input() set excludeDays(data: number[]) {
    if (data) {
      this.invalidDates = data;
    }
  }

  @Input() set isValidationTriggered(trigger: boolean) {
    if (trigger) {
      markAsTouchedAndUpdate(this.dateControl);
      this.cd.markForCheck();
    }
  }

  startTime: number | Moment | Date;
  dateControl = new UntypedFormControl();

  isDisabled: boolean;

  invalidDates: number[] = [];
  dateFormat = this.isHourOnlyMode ? DATEPICKER_DISPLAY_HOUR_ONLY_FORMAT : DATEPICKER_DISPLAY_DATE_FORMAT;
  iconNames = IconNames;
  subscriptions: Subscription[] = [];

  isFieldRequired: (control: UntypedFormControl | AbstractControl) => string = markRequiredControlWithAsteriskSymbol;

  constructor(
    @Self() public ngControl: NgControl,
    public dateTimeAdapter: DateTimeAdapter<Date>,
    private appFacade: AppFacade,
    private cd: ChangeDetectorRef,
    @Inject(OWL_DATE_TIME_FORMATS) private owlDateTimeFormats: OwlDateTimeFormats,
    private authFacade: AuthFacade,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.setStartTimeForHourMode();
    this.setLocaleValueSubscription();
    this.initDateControlValueSubscription();
    this.initDateControlValidationSubscription();
    this.setTimeFormatSubscription();
  }

  registerOnChange(fn: () => number): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.dateControl.disable({ emitEvent: false }) : this.dateControl.enable({ emitEvent: false });

    this.isDisabled = isDisabled;
  }

  writeValue(dateTime: Date | Moment | number): void {
    if (this.isHourOnlyMode && typeof dateTime === 'number') {
      const hourMoment = moment().hour(dateTime).minute(0).second(0);

      this.startTime = hourMoment;

      this.dateControl.setValue(hourMoment, { emitEvent: false });

      return;
    }

    const dateTimeToMoment = dateTime ? moment(dateTime) : dateTime;

    if (dateTimeToMoment) {
      this.startTime = dateTimeToMoment;
    }

    this.dateControl.setValue(dateTimeToMoment, {
      emitEvent: false,
    });
  }

  onDateValueChanged(value: Moment): void {
    if (value.isSame(moment(), 'day')) {
      return;
    }

    const startOfSelectedDay = value.clone().startOf('day');

    const container = (this.picker as unknown as { pickerContainer?: { pickerMoment: Moment } }).pickerContainer;
    if (container) {
      container.pickerMoment = startOfSelectedDay;
    }
  }

  get isHourOnlyMode(): boolean {
    return this.pickerType === 'timer' && this.changeHourOnly;
  }

  private onChange: (value: number) => number;

  private onTouched: () => void;

  private initDateControlValueSubscription(): void {
    const subscription = this.dateControl.valueChanges.subscribe((value: Moment) => {
      if (value) {
        this.startTime = value;

        if (this.isHourOnlyMode) {
          this.onChange(value.hour());
        } else {
          this.onChange(value.valueOf());
        }
      }
    });

    this.subscriptions.push(subscription);
  }

  private setLocaleValueSubscription(): void {
    this.subscriptions.push(
      this.appFacade.currentLanguage$.pipe(filter(Boolean)).subscribe((language: Language) => {
        this.dateTimeAdapter.setLocale(LOCALE_CODE_LIST[language]);
      }),
    );
  }

  private initDateControlValidationSubscription(): void {
    if (this.ngControl) {
      this.dateControl.setValidators(this.ngControl.control?.validator);

      const subscription = this.ngControl.statusChanges.subscribe(() => {
        this.cd.markForCheck();
      });

      this.subscriptions.push(subscription);
    }
  }

  private setTimeFormatSubscription(): void {
    this.subscriptions.push(
      this.authFacade.isTwentyFourHourTime$.pipe(take(1)).subscribe((is24h: boolean) => {
        this.owlDateTimeFormats.fullPickerInput = is24h
          ? DATE_TIME_PICKER_DISPLAY_DATE_FORMAT
          : DATE_TIME_PICKER_DISPLAY_DATE_12H_FORMAT;
      }),
    );
  }

  private setStartTimeForHourMode(): void {
    if (this.isHourOnlyMode) {
      this.startTime = moment().minute(0).second(0).millisecond(0);
    }
  }
}
